import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonComponent } from 'ui-elements/button';
import { FormFieldModule } from 'ui-elements/form-field';
import { BaseInputDirective } from 'ui-elements/form/input/base-input.directive';
import { StayInTouchFormComponent } from './stay-in-touch-form/stay-in-touch-form.component';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngxs/store';
import { ShellState } from '../../shell.state';
import { ForbiddenDomainsValidatorDirective } from 'common';
import { BrochureService, StayInTouchRequest } from 'api';
import { LoadingModalComponent } from '../../../shared/components/loading-modal/loading-modal.component';
import { NotificationModalServiceService } from '../../../shared/services/notification-modal-service.service';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { SegmentIdentifyTraits, SegmentService } from 'segment';

@Component({
  selector: 'ss-www-stay-in-touch',
  imports: [
    CommonModule,
    BaseInputDirective,
    FormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    StayInTouchFormComponent,
    ForbiddenDomainsValidatorDirective,
    LoadingModalComponent,
  ],
  templateUrl: './stay-in-touch.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StayInTouchComponent implements OnDestroy {
  private readonly segmentService = inject(SegmentService);
  footerContent$ = inject(Store).select(ShellState.footerContent);
  form = new FormGroup({
    emailAddress: new FormControl('', [Validators.required, Validators.email]),
  });

  showFormModal$ = new BehaviorSubject<{ email: string; showModal: boolean }>({
    showModal: false,
    email: '',
  });
  isSubmitting = signal(false);
  private brochureService = inject(BrochureService);
  private notificationService = inject(NotificationModalServiceService);
  private destroy$ = new Subject<void>();
  private analyticsService = inject(AnalyticsService);

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  signInClick() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.controls.emailAddress.updateValueAndValidity();
      return;
    }

    const { emailAddress } = this.form.getRawValue();
    this.analyticsService.trackBeginStayInTouch();
    const traits: SegmentIdentifyTraits = {
      email: emailAddress ?? undefined,
    };
    this.segmentService.identifyUser(undefined, traits);
    this.segmentService.trackEvent('Signup Step Completed', {
      traits,
      form: 'Stay In Touch',
    });
    this.showFormModal$.next({
      showModal: true,
      email: emailAddress || '',
    });
  }

  onOpenModalChange(newValue: boolean) {
    this.showFormModal$.next({
      ...this.showFormModal$.value,
      showModal: newValue,
    });
  }

  private identifyAndTrackEvent(request: StayInTouchRequest) {
    const { contact, address } = request.customer;
    const traits = {
      email: contact.emailAddress,
      firstName: contact.firstName,
      lastName: contact.lastName,
      phone: contact.phoneNumber,
      address: {
        street: address.addressLine1,
        city: address.city,
        state: address.state,
        postalCode: address.zipcode,
        country: address.country,
      },
    };
    this.segmentService.identifyUser(undefined, traits);
    this.segmentService.trackEvent('Form Submitted', {
      traits,
      form: 'Stay In Touch',
    });
  }

  onFormSubmit(request: StayInTouchRequest) {
    this.onOpenModalChange(false);
    this.isSubmitting.set(true);
    this.brochureService
      .submitStayInTouch(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.analyticsService.trackCompleteStayInTouch();
        this.identifyAndTrackEvent(request);
        this.isSubmitting.set(false);
        this.notificationService.showMessage({
          title: 'Stay in Touch',
          message: 'Thank you for signing up!',
          closeButtonMessage: 'Close',
        });
      });
  }
}
